/**
 * THE "‹ BACK" PILL over the core auth screens, rebuilt on the design tokens
 * (owner, 21.09 — he pasted its hand-written inline style block and asked for
 * it to feel like part of the game).
 *
 * It lets a visitor who hit Log In / Sign Up bail back to the marketing
 * landing at `/`. Core owns the auth markup, so the control is created from
 * the game side and parked on `document.body`, the same way the ambient loop
 * and the brand lockup ride `.bg-auth-gradient`.
 *
 * It used to be ~18 lines of hard-coded hex in `style.cssText`: a theme change
 * could never reach it, and neither could `prefers-reduced-motion`. It is a
 * bronze plate written in Tailwind utilities now — the element renders through
 * a class, so nothing here needs a custom class name or an inline style:
 *
 *   • `fixed` top-left, inset by `max(1rem, env(safe-area-inset-*))`, so a
 *     notched phone in landscape pushes it clear of the cutout instead of
 *     parking it under one;
 *   • `min-h-11` = 44px, the tap-target floor, with the padding on top of it;
 *   • a dark plate between bronze rules, square-cornered — the game's own
 *     material, not a glass pill;
 *   • Cormorant SC small-caps at 12px. It is well under the ~16px floor, so it
 *     stays on the LABEL face and not on Legend Sacred, whose hairlines break
 *     up at that size — the two-face heading rule, applied.
 *   • hover and `focus-visible` are both visible, and the transition is behind
 *     `motion-safe` rather than running unconditionally.
 */
export const BACK_PILL_CLASS =
  'fixed z-[1000] top-[max(1rem,env(safe-area-inset-top))] left-[max(1rem,env(safe-area-inset-left))] '
  + 'inline-flex min-h-11 items-center gap-2 rounded-none border border-bronze-lit/50 '
  + 'bg-linear-to-b from-surface via-bg to-surface-2 px-5 py-2 '
  + 'font-label text-[13px] font-semibold uppercase tracking-[0.08em] text-gold-bright '
  + 'shadow-[inset_0_1px_0_rgba(232,230,223,0.10),0_2px_10px_rgba(0,0,0,0.45)] '
  + 'hover:border-gold/70 hover:text-marble hover:bg-surface '
  + 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-veil '
  + 'motion-safe:transition-colors motion-safe:duration-200';

/** Mount the pill. Returns a teardown that removes it. */
export function addBackToLanding(onBack: () => void, doc: Document = document): () => void {
  const btn = doc.createElement('button');
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Back to home');
  btn.className = BACK_PILL_CLASS;
  btn.innerHTML = '<span aria-hidden="true">&#8249;</span> Back';
  btn.onclick = () => onBack();
  doc.body.appendChild(btn);
  return () => btn.remove();
}
